import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";
import { toast } from "sonner";

const YachtCtx = createContext(null);

// Module-level emitter so the axios interceptor can ask YachtProvider to refresh
// (interceptors live outside React).
const yachtEvents = new EventTarget();
export const requestYachtRefresh = (reason = "axios-403") =>
  yachtEvents.dispatchEvent(new CustomEvent("refresh", { detail: { reason } }));

export function YachtProvider({ children }) {
  const { user } = useAuth();
  const [yachts, setYachts] = useState([]);
  const [activeYacht, setActiveYacht] = useState(null);
  // Start in loading=true so callers (e.g. AppIndex) don't redirect to /yachts
  // before the very first fetch completes.
  const [loading, setLoading] = useState(true);
  const lastYachtIdsRef = useRef(new Set());

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const { data } = await api.get("/yachts");
      const newIds = new Set(data.map((y) => y.id));

      // Detect a yacht that vanished since the previous load — happens after an
      // approver finalises a deletion the user requested. Notify the requester.
      const removed = [...lastYachtIdsRef.current].filter((id) => !newIds.has(id));
      lastYachtIdsRef.current = newIds;

      setYachts(data);
      const savedId = localStorage.getItem("activeYachtId");
      const found = data.find((y) => y.id === savedId) || data[0] || null;
      setActiveYacht(found);
      if (found && found.id !== savedId) {
        localStorage.setItem("activeYachtId", found.id);
      } else if (!found && savedId) {
        localStorage.removeItem("activeYachtId");
      }

      if (removed.length && savedId && removed.includes(savedId)) {
        toast.info("This yacht was removed. Switched to your next yacht.");
      }
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  // NOTE: previously this provider polled /api/yachts every 20s to detect
  // external state changes (an approver finalising a deletion from email,
  // a senior promoting/demoting a crew member on another device). Polling
  // was removed because it caused two real bugs:
  //   - The Logbook engine-readings dialog had its in-progress edits
  //     overwritten every poll cycle.
  //   - Dashboard widgets visibly flickered every 20s.
  // We now refresh on three deterministic triggers instead:
  //   1. Initial mount (above).
  //   2. Every route change inside the app shell (see AppShell).
  //   3. On-demand via `requestYachtRefresh()` from the axios interceptor
  //      after a 403/yacht-vanished response.

  // On-demand refresh requests from the axios interceptor
  useEffect(() => {
    const onRefresh = () => { load(); };
    yachtEvents.addEventListener("refresh", onRefresh);
    return () => yachtEvents.removeEventListener("refresh", onRefresh);
  }, [load]);

  const switchYacht = (y) => {
    setActiveYacht(y);
    if (y?.id) localStorage.setItem("activeYachtId", y.id);
  };

  return (
    <YachtCtx.Provider value={{ yachts, activeYacht, switchYacht, refresh: load, loading }}>
      {children}
    </YachtCtx.Provider>
  );
}

export const useYacht = () => useContext(YachtCtx);
